import { useCallback, useEffect, useState } from "react";
import { Plus, Film, Image as ImageIcon, Trash2, Pencil, Eye, EyeOff, UploadCloud, Loader2, FileText } from "lucide-react";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadCreatorFile, MAX_IMAGE_MB, MAX_STREAM_VIDEO_MB } from "@/lib/upload";
import { MediaImage } from "@/components/Media";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const CATEGORIES = ["Lifestyle", "Bikinis", "Swimwear", "Glamour", "Fashion", "Behind the Scenes", "Travel"];
const ACCESS = [
  { v: "public", label: "Public preview (any user)" },
  { v: "subscriber", label: "Registered subscriber" },
  { v: "member", label: "Membership-included (your members + All Access)" },
  { v: "ppv", label: "Pay-per-view (one-time unlock)" },
  { v: "creator", label: "Creator only" },
];
const ACCESS_LABEL = { public: "Preview", subscriber: "Subscriber", member: "Members", ppv: "Pay-per-view", creator: "Creator only" };

const EMPTY = { type: "album", title: "", description: "", category: "Lifestyle", access_level: "subscriber", ppv_price: "" };
const KINDS = {
  album: { title: "My Photos", sub: "Upload and publish photo albums.", empty: "No photo albums yet.", btn: "New album" },
  video: { title: "My Videos", sub: "Upload videos — they process through Cloudflare Stream.", empty: "No videos yet.", btn: "New video" },
  post: { title: "My Posts", sub: "Write updates for your fans, with optional photos.", empty: "No posts yet.", btn: "New post" },
};
const TABS = [["/creator/content", "All"], ["/creator/photos", "Photos"], ["/creator/videos", "Videos"], ["/creator/posts", "Posts"]];

function Tabs() {
  return (
    <div data-testid="content-tabs" className="mt-4 flex gap-2 overflow-x-auto no-scrollbar">
      {TABS.map(([to, l]) => (
        <NavLink key={to} to={to} end data-testid={`content-tab-${l.toLowerCase()}`}
          className={({ isActive }) => `rounded-full border px-4 py-1.5 text-xs ${isActive ? "border-[#a03cff] bg-[#3a0d6e]/60" : "border-white/15 text-[#a7a0af]"}`}>{l}</NavLink>
      ))}
    </div>
  );
}

export default function Content({ kind }) {
  const meta = KINDS[kind] || { title: "My Content", sub: "Upload, organize, and publish your photos, videos and posts.", empty: "No content yet", btn: "New Content" };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/creator/content").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const processing = items.some((it) => it.type === "video" && it.video_state !== "error" && !it.thumb_failed && (it.video_ready === false ||
    (!it.cover_url && Date.now() - new Date(it.created_at).getTime() < 10 * 60 * 1000)));
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(() => api.get("/creator/content").then((r) => setItems(r.data.items || [])).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [processing]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, type: kind || "album" });
    setFiles([]);
    setCoverFile(null);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ type: it.type, title: it.title, description: it.description || "", category: it.category || "Lifestyle", access_level: it.access_level, ppv_price: it.ppv_price ?? "" });
    setFiles([]);
    setCoverFile(null);
    setOpen(true);
  };

  const uploadOne = async (file) => {
    const data = await uploadCreatorFile(file, (p) => toast.loading(`Uploading ${file.name} — ${p}%`, { id: "upload-progress" }));
    toast.dismiss("upload-progress");
    return data;
  };

  const save = async (publish) => {
    if (!form.title.trim()) return toast.error("Please enter a title.");
    const ppv = form.access_level === "ppv" ? { ppv_price: Number(form.ppv_price) } : {};
    if (form.access_level === "ppv" && !(Number(form.ppv_price) >= 1)) return toast.error("Set a pay-per-view price of at least $1.");
    setBusy(true);
    try {
      if (editing) {
        const extra = {};
        if (coverFile) extra.cover_asset_id = (await uploadOne(coverFile)).id;
        await api.put(`/creator/content/${editing.id}`, {
          title: form.title, description: form.description, category: form.category, access_level: form.access_level, ...ppv, ...extra,
        });
        if (publish !== null) await api.post(`/creator/content/${editing.id}/${publish ? "publish" : "unpublish"}`);
        toast.success("Content updated.");
      } else {
        if (files.length === 0 && form.type !== "post") return toast.error("Please add at least one file.");
        if (form.type === "post" && files.length === 0 && !form.description.trim()) return toast.error("Write something or add a photo.");
        const uploaded = [];
        for (const f of files) uploaded.push(await uploadOne(f));
        let payload = {
          type: form.type, title: form.title, description: form.description,
          category: form.category, access_level: form.access_level, publish: !!publish, ...ppv,
        };
        if (form.type === "video") {
          payload.video_asset_id = uploaded[0].id;
          if (coverFile) {
            const c = await uploadOne(coverFile);
            payload.cover_asset_id = c.id;
            payload.thumb_asset_id = c.id;
          }
        } else {
          payload.asset_ids = uploaded.map((u) => u.id);
          payload.cover_asset_id = uploaded[0]?.id || null;
        }
        await api.post("/creator/content", payload);
        toast.success(publish ? "Published." : "Saved as draft.");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.dismiss("upload-progress");
      toast.error(err.response ? formatApiErrorDetail(err.response?.data?.detail) : err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (it) => {
    try {
      await api.post(`/creator/content/${it.id}/${it.status === "published" ? "unpublish" : "publish"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/creator/content/${it.id}`);
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const shown = kind ? items.filter((it) => it.type === kind) : items;
  return (
    <div data-testid="creator-content">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 data-testid="content-heading" className="text-2xl font-extrabold">{meta.title}</h1>
          <p className="text-sm text-[#a7a0af]">{meta.sub}</p>
        </div>
        <button
          onClick={openNew}
          data-testid="creator-new-content-btn"
          className="btn-brand inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus size={16} /> {meta.btn}
        </button>
      </div>
      <Tabs />

      {loading ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-white/5" />)}
        </div>
      ) : shown.length === 0 ? (
        <div data-testid="creator-content-empty" className="mt-6 rounded-2xl glass p-12 text-center">
          <UploadCloud size={30} className="mx-auto text-[#6f6878]" />
          <p className="mt-3 text-sm font-semibold">{meta.empty}</p>
          <p className="mt-1 text-xs text-[#a7a0af]">Tap “{meta.btn}” to create one. Save as a draft or publish straight away.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {shown.map((it) => (
            <div key={it.id} data-testid={`creator-content-${it.id}`} className="overflow-hidden rounded-2xl glass">
              <div className="relative aspect-[4/3]">
                {it.cover_url ? <MediaImage url={it.cover_url} alt={it.title} className="h-full w-full object-cover" /> : it.type === "post" ? (
                  <div className="h-full w-full overflow-hidden bg-gradient-to-br from-[#1a0b2e] to-[#0c0a10] p-3 pt-8 text-xs text-white/80">{it.description}</div>
                ) : (
                  <div data-testid={`content-thumb-placeholder-${it.id}`} className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gradient-to-br from-[#1a0b2e] to-[#0c0a10] text-[0.6rem] text-[#a7a0af]">
                    {it.type === "video" && it.video_state !== "error" && !it.thumb_failed ? <Loader2 size={18} className="animate-spin text-[#a03cff]" /> : <Film size={20} className="text-[#6f6878]" />}
                    {it.type === "video" ? (it.video_state === "error" || it.thumb_failed ? "No thumbnail — upload a cover" : "Preparing thumbnail…") : "No cover"}
                  </div>
                )}
                {it.cover_auto && <span data-testid={`content-auto-thumb-${it.id}`} className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[0.55rem]">Auto thumbnail</span>}
                <span className="absolute left-2 top-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[0.6rem] capitalize">
                  {it.type === "video" ? <Film size={11} /> : it.type === "post" ? <FileText size={11} /> : <ImageIcon size={11} />} {it.type === "album" ? "photos" : it.type}
                </span>
                <span
                  data-testid={`content-status-${it.id}`}
                  className={`absolute right-2 top-2 rounded px-1.5 py-0.5 text-[0.55rem] font-semibold ${
                    it.status === "published" ? "bg-emerald-500/80" : "bg-white/20"
                  }`}
                >
                  {it.status}
                </span>
                {it.type === "video" && it.video_ready === false && (
                  <span data-testid={`content-video-state-${it.id}`} className={`absolute bottom-2 left-2 rounded px-1.5 py-0.5 text-[0.55rem] font-semibold ${it.video_state === "error" ? "bg-red-500/80" : "bg-[#8a2be2]/80"}`}>
                    {it.video_state === "error" ? "Processing failed — delete and re-upload" : "Processing video…"}
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="truncate text-sm font-semibold">{it.title}</p>
                <p className="truncate text-[0.65rem] text-[#a7a0af]">
                  {it.category} · {ACCESS_LABEL[it.access_level]}
                </p>
                <div className="mt-3 flex items-center gap-1.5">
                  <button
                    onClick={() => togglePublish(it)}
                    data-testid={`content-publish-toggle-${it.id}`}
                    className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-white/10 py-1.5 text-[0.65rem] font-medium hover:border-[#d42be8]/40"
                  >
                    {it.status === "published" ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                  <button onClick={() => openEdit(it)} data-testid={`content-edit-${it.id}`} className="rounded-lg border border-white/10 p-1.5 hover:border-white/30">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(it)} data-testid={`content-delete-${it.id}`} className="rounded-lg border border-white/10 p-1.5 text-red-300 hover:border-red-400/40">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0c0a10] text-white no-scrollbar">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit content" : "New content"}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {!editing && (
              <div>
                <Label className="text-xs text-[#a7a0af]">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="content-type-select" className="mt-1 border-white/10 bg-white/5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="album">Photo album</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="post">Post</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs text-[#a7a0af]">Title</Label>
              <Input
                data-testid="content-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Golden Hour"
                className="mt-1 border-white/10 bg-white/5"
              />
            </div>

            <div>
              <Label className="text-xs text-[#a7a0af]">{form.type === "post" ? "Post text" : "Description"}</Label>
              <Textarea
                data-testid="content-desc-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A closer look…"
                className="mt-1 border-white/10 bg-white/5"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#a7a0af]">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="content-category-select" className="mt-1 border-white/10 bg-white/5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#a7a0af]">Access</Label>
                <Select value={form.access_level} onValueChange={(v) => setForm({ ...form, access_level: v })}>
                  <SelectTrigger data-testid="content-access-select" className="mt-1 border-white/10 bg-white/5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCESS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.access_level === "ppv" && (
                  <input type="number" min="1" max="500" step="0.01" inputMode="decimal" value={form.ppv_price} onChange={(e) => setForm({ ...form, ppv_price: e.target.value })}
                    data-testid="content-ppv-price" placeholder="Unlock price (USD)" className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm" />
                )}
              </div>
            </div>

            {editing && editing.type === "video" && (
              <div data-testid="content-edit-cover" className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <Label className="text-xs text-[#a7a0af]">Cover image {editing.cover_auto ? "(currently the automatic Cloudflare thumbnail)" : editing.cover_url ? "(your custom cover)" : "(none yet)"}</Label>
                <input data-testid="content-edit-cover-input" type="file" accept="image/*" onChange={(e) => setCoverFile((e.target.files || [])[0] || null)}
                  className="block w-full text-xs text-[#a7a0af] file:mr-3 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-white" />
                {!editing.cover_auto && !editing.thumb_failed && editing.video_ready !== false && (
                  <button type="button" data-testid="content-use-auto-thumb" onClick={async () => {
                    try { await api.put(`/creator/content/${editing.id}`, { use_auto_cover: true }); toast.success("Using the Cloudflare thumbnail"); setOpen(false); load(); }
                    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                  }} className="self-start rounded-full border border-white/15 px-3 py-1 text-xs">Use automatic thumbnail</button>
                )}
              </div>
            )}

            {!editing && (
              <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div>
                  <Label className="text-xs text-[#a7a0af]">
                    {form.type === "video" ? `Video file (mp4, webm, mov · up to ${MAX_STREAM_VIDEO_MB / 1024} GB / 30 min via Cloudflare Stream)` : `Photos${form.type === "post" ? " (optional)" : ""} (jpg, png, webp · max ${MAX_IMAGE_MB}MB each)`}
                  </Label>
                  <input
                    data-testid="content-file-input"
                    type="file"
                    accept={form.type === "video" ? "video/*" : "image/*"}
                    multiple={form.type !== "video"}
                    onChange={(e) => setFiles(Array.from(e.target.files || []))}
                    className="mt-1 block w-full text-xs text-[#a7a0af] file:mr-3 file:rounded-full file:border-0 file:bg-[#6a00ff]/30 file:px-3 file:py-1.5 file:text-white"
                  />
                  {files.length > 0 && <p className="mt-1 text-[0.65rem] text-[#a7a0af]">{files.length} file(s) selected</p>}
                </div>
                {form.type === "video" && (
                  <div>
                    <Label className="text-xs text-[#a7a0af]">Cover image (optional — if you skip it, a thumbnail is taken from the video automatically)</Label>
                    <input
                      data-testid="content-cover-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCoverFile((e.target.files || [])[0] || null)}
                      className="mt-1 block w-full text-xs text-[#a7a0af] file:mr-3 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-white"
                    />
                  </div>
                )}
                <p className="text-[0.65rem] text-[#6f6878]">Uploads are stored privately. Content is never auto-published.</p>
              </div>
            )}
          </div>

          <DialogFooter className="mt-2 flex-row gap-2 sm:justify-end">
            <button
              onClick={() => save(editing ? null : false)}
              disabled={busy}
              data-testid="content-save-draft"
              className="flex-1 rounded-xl border border-white/15 py-3 text-sm font-semibold hover:border-white/30 sm:flex-none sm:px-5"
            >
              {busy ? <Loader2 className="mx-auto animate-spin" size={16} /> : editing ? "Save changes" : "Save draft"}
            </button>
            <button
              onClick={() => save(true)}
              disabled={busy}
              data-testid="content-publish-btn"
              className="btn-brand flex-1 rounded-xl py-3 text-sm font-semibold text-white sm:flex-none sm:px-5"
            >
              Publish
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
